import { AxiosError, CancelToken } from 'axios';
import { plainToClass } from 'class-transformer';
import { clearCompanyDetailsCache } from '../../api-utils';
import authApi from '../auth-api';
import {
  AddCompanyUserRequest,
  CompanyApiKeyListModel,
  CompanyDetailModel,
  CompanyModel,
  CompanyRenewalStatusModel,
  CompanySearchModel,
  CompanyUserModel,
  SetPrimaryContactRequest,
} from './models';

/**
 * Provides access to the `/company` auth endpoints.
 */
class CompanyApi {
  private resource = 'company';

  /**
   * Search companies available to the current user.
   */
  searchCompanies = async (
    keyword: string,
    take = 50,
    token?: CancelToken,
  ): Promise<CompanySearchModel[]> => {
    const response = await authApi.get(
      `${this.resource}/search`,
      {
        params: {
          keyword,
          take,
        },
        cancelToken: token,
        cache: { maxAge: 0 },
      },
    );

    return plainToClass(CompanySearchModel, response.data as []);
  }

  /**
   * Create a new company using the auth API.
   */
  createCompany = async (company: CompanyModel): Promise<CompanyModel> => {
    const response = await authApi.post(
      `${this.resource}`,
      company,
    );

    return plainToClass(CompanyModel, response.data);
  }

  /**
   * Retrieve a single company by identifier.
   */
  getCompany = async (id: number): Promise<CompanyModel> => {
    const response = await authApi.get(
      `${this.resource}/${id}`,
      {
        headers: {
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
          Expires: '0',
        },
        cache: { maxAge: 0 },
      },
    );

    return plainToClass(CompanyModel, response.data);
  }

  /**
   * Retrieve the renewal status associated with the company.
   */
  getCompanyRenewalStatus = async (id: number): Promise<CompanyRenewalStatusModel> => {
    const response = await authApi.get(
      `${this.resource}/${id}/renewalstatus`,
      {
        headers: {
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
          Expires: '0',
        },
        cache: { maxAge: 0 },
      },
    );

    return plainToClass(CompanyRenewalStatusModel, response.data);
  }

  /**
   * Retrieve detailed company information, including flocks and users.
   */
  getCompanyDetail = async (id: number, bypassCache = false): Promise<CompanyDetailModel> => {
    const config = bypassCache
      ? {
          cache: { maxAge: 0 },
          // Also add cache-busting header for extra reliability
          headers: {
            'Cache-Control': 'no-cache',
            Pragma: 'no-cache',
            Expires: '0',
          },
        }
      : {};

    const response = await authApi.get(
      `${this.resource}/${id}/detail`,
      config,
    );

    return plainToClass(CompanyDetailModel, response.data);
  }

  /**
   * Retrieve the list of users for a company.
   */
  getCompanyUsers = async (id: number, token?: CancelToken): Promise<CompanyUserModel[]> => {
    const response = await authApi.get(
      `${this.resource}/${id}/users`,
      { cancelToken: token, cache: { maxAge: 0 } },
    );

    return plainToClass(CompanyUserModel, response.data as []);
  }

  /**
   * Retrieve the list of API keys for a company.
   */
  getCompanyApiKeys = async (id: number, token?: CancelToken): Promise<CompanyApiKeyListModel[]> => {
    const response = await authApi.get(
      `${this.resource}/${id}/apikeys`,
      { cancelToken: token, cache: { maxAge: 0 } },
    );

    return plainToClass(CompanyApiKeyListModel, response.data as []);
  }

  /**
   * Retrieve the current user's company detail.
   */
  getMyCompanyDetail = async (bypassCache = false, token?: CancelToken): Promise<CompanyDetailModel | null> => {
    const config: {
      cancelToken?: CancelToken;
      cache?: { maxAge: number };
      headers?: Record<string, string>;
    } = { cancelToken: token };
    if (bypassCache) {
      config.cache = { maxAge: 0 };
      config.headers = {
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache',
        Expires: '0',
      };
    }

    try {
      const response = await authApi.get(
        `${this.resource}/detail`,
        config,
      );

      return plainToClass(CompanyDetailModel, response.data);
    } catch (error) {
      const axiosError = error as AxiosError;
      if (axiosError?.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  /**
   * Inactivate a company.
   */
  inactivateCompany = async (id: number, token?: CancelToken): Promise<boolean> => {
    try {
      await authApi.get(
        `${this.resource}/${id}/inactivate`,
        { cancelToken: token, cache: { maxAge: 0 } },
      );

      await clearCompanyDetailsCache(id);
      return true;
    } catch (error) {
      const axiosError = error as AxiosError;
      if (axiosError?.response?.status === 404) {
        return false;
      }
      throw error;
    }
  }

  /**
   * Update an existing company.
   */
  updateCompany = async (id: number, company: CompanyModel): Promise<boolean> => {
    const response = await authApi.put(
      `${this.resource}/${id}`,
      company,
    );

    // Clear cached entries for this company to ensure fresh data on next fetch
    await clearCompanyDetailsCache(id);

    return Boolean(response.data);
  }

  /**
   * Add an existing user to a company.
   */
  addUserToCompany = async (
    id: number,
    request: AddCompanyUserRequest,
    token?: CancelToken,
  ): Promise<boolean> => {
    try {
      await authApi.post(
        `${this.resource}/${id}/users`,
        request,
        { cancelToken: token },
      );

      // Clear cached entries for this company to ensure fresh data on next fetch
      await clearCompanyDetailsCache(id);

      return true;
    } catch (error) {
      const axiosError = error as AxiosError;
      if (axiosError?.response?.status === 404) {
        return false;
      }
      throw error;
    }
  }

  /**
   * Add an existing user to the current user's company.
   */
  addUserToMyCompany = async (
    request: AddCompanyUserRequest,
    token?: CancelToken,
  ): Promise<boolean> => {
    try {
      await authApi.post(
        `${this.resource}/mycompany/users`,
        request,
        { cancelToken: token },
      );

      return true;
    } catch (error) {
      const axiosError = error as AxiosError;
      if (axiosError?.response?.status === 404) {
        return false;
      }
      throw error;
    }
  }

  /**
   * Remove a user from the current user's company.
   */
  removeUserFromMyCompany = async (userId: number, token?: CancelToken): Promise<boolean> => {
    try {
      await authApi.delete(
        `${this.resource}/mycompany/users/${userId}`,
        { cancelToken: token },
      );

      return true;
    } catch (error) {
      const axiosError = error as AxiosError;
      if (axiosError?.response?.status === 404) {
        return false;
      }
      throw error;
    }
  }

  /**
   * Remove a user from a company.
   */
  removeUserFromCompany = async (id: number, userId: number, token?: CancelToken): Promise<boolean> => {
    try {
      await authApi.delete(
        `${this.resource}/${id}/users/${userId}`,
        { cancelToken: token },
      );

      await clearCompanyDetailsCache(id);
      return true;
    } catch (error) {
      const axiosError = error as AxiosError;
      if (axiosError?.response?.status === 404) {
        return false;
      }
      throw error;
    }
  }

  /**
   * Sets or unsets a primary contact for a company user.
   */
  setPrimaryContact = async (
    id: number,
    request: SetPrimaryContactRequest,
    token?: CancelToken,
  ): Promise<boolean> => {
    try {
      await authApi.put(
        `${this.resource}/${id}/setprimary`,
        request,
        { cancelToken: token },
      );

      // Clear cached entries for this company to ensure fresh data on next fetch
      await clearCompanyDetailsCache(id);

      return true;
    } catch (error) {
      const axiosError = error as AxiosError;
      if (axiosError?.response?.status === 404) {
        return false;
      }
      throw error;
    }
  }
}

export default new CompanyApi();
